import React, { useRef, useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import cn from "classnames";
import "swiper/css";
import "swiper/css/pagination";
import { Image, Card } from "react-bootstrap";
import { ASSESTS } from "../../../constant/assest";
import styles from "./TopInvitersSlider.module.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTopInvitersData } from "../../../redux/slices/referralTracker/actions";
import { TopInvitersUser } from "../../../redux/slices/referralTracker/types";

export interface ITopInvitersSliderProps {
  className?: string;
}

const getMedal = (rank: number): React.ReactNode => {
  switch (rank) {
    case 1:
      return <Image src={ASSESTS.FIRST} alt="1" width={16} height={16} />;
    case 2:
      return <Image src={ASSESTS.SECOND} alt="2" width={16} height={16} />;
    case 3:
      return <Image src={ASSESTS.THIRD} alt="3" width={16} height={16} />;
    default:
      return <span className={styles.ranking}>#{rank}</span>;
  }
};

const TopInvitersSlider: React.FC<ITopInvitersSliderProps> = ({
  className,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { topInviters, topInvitersLoading } = useSelector(
    (state: RootState) => state.referralTracker
  );

  const swiperRef = useRef<SwiperClass | null>(null);

  const [pagesData, setPagesData] = useState<Record<number, TopInvitersUser[]>>(
    {}
  );
  const [totalSlides, setTotalSlides] = useState(0);

 // ---- INITIAL LOAD ----
  useEffect(() => {
    dispatch(fetchTopInvitersData({ page: 1, limit: 5 }));
  }, [dispatch]);

  // ---- WHEN API RETURNS DATA ----
  useEffect(() => {
    if (!topInviters || topInvitersLoading === "pending") return;

    setPagesData((prev) => ({
      ...prev,
      [topInviters.page - 1]: topInviters.results,
    }));

    if (totalSlides === 0) {
      setTotalSlides(topInviters.totalPages);
    }
  }, [topInviters, topInvitersLoading, totalSlides]);

  // ---- SLIDE CHANGE ----
  const handleSlideChange = (swiper:any) => {
    const index = swiper.activeIndex;

    // If we don’t have data for this page → fetch it
    if (!pagesData[index]) {
      dispatch(fetchTopInvitersData({ page: index + 1, limit: 5 }));
    }
  };

  // const handlePaginationClick = (index: number) => {
  //   swiperRef.current?.slideTo(index);
  // };

  return (
    <Card bsPrefix={cn(styles.topInvitersCard, className)}>
      <Card.Header bsPrefix={styles.header}>
        <Card.Title as={"div"} bsPrefix={styles.title}>
          <Image src={ASSESTS.STAR} alt="Top Inviters" width={20} height={20} />
          <span>Top Inviters</span>
        </Card.Title>
      </Card.Header>

      <Card.Body bsPrefix={styles.body}>
        <Swiper
          modules={[Pagination]}
          pagination={{ clickable: true }}
          onSwiper={(swiper: SwiperClass) => (swiperRef.current = swiper)}
          onSlideChange={handleSlideChange}
        >
          {Array.from({ length: totalSlides }).map((_, pageIndex) => (
            <SwiperSlide key={pageIndex}>
              <div className={styles.slideWrap}>
                {topInvitersLoading === "pending" &&
                  !pagesData[pageIndex]  ? (
                  // Skeleton loader
                  [...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={cn(styles.slideItem, styles.skeleton)}
                    >
                      <div className={styles.rank}></div>
                      <div className={styles.detail}>
                        <div className={styles.name}></div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Actual data
                  pagesData[pageIndex]?.map((user) => (
                    <div
                      key={user.rank}
                      className={cn(
                        styles.slideItem,
                        user.rank > 5 && styles.bigItem
                      )}
                    >
                      <div className={styles.rank}>{getMedal(user.rank)}</div>
                      <div className={styles.detail}>
                        <div className={styles.name}>{user.full_name}</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* <Card.Footer bsPrefix={styles.customPagination}>
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={cn(
                styles.customDot,
              )}
              onClick={() => handlePaginationClick(index)}
            />
          ))}
        </Card.Footer> */}
      </Card.Body>
    </Card>
  );
};

export default TopInvitersSlider;